/**
 * Product Buy Box runtime.
 *
 * Responsibilities:
 *   1. Read the SSR'd pool from <script data-spectrum-vghxskvp-pool> (options,
 *      variants, shop money format, savings config).
 *   2. On swatch click — recompute the (option1, option2, option3) tuple, resolve
 *      the matching variant, and repaint THIS box's price / compare-at / savings
 *      / selected-state / add-to-cart availability. There is no `?variant=` URL
 *      write: the box owns a product picked by a prop, so it must not rewrite the
 *      page's variant param (that belongs to the PDP the page is actually on).
 *   3. Quantity stepper — clamp to a minimum of 1.
 *   4. Add-to-cart / buy-now — add the resolved variant × quantity via
 *      window.Spectrum.cart, opening the theme's cart UI on add and jumping to
 *      checkout on buy-now. Buy-now navigation is suppressed inside the Studio
 *      preview iframe.
 *
 * Theme-agnostic — depends only on window.Spectrum.cart + the SSR pool.
 */
;(() => {
  const SNIPPET_ID = 'vghxskvp'
  const FEATURE_SLUG = 'buy_box'
  const QTY_MAX = 99

  function noopTrack() {}
  function safeTrack(track) {
    if (typeof track !== 'function') return noopTrack
    return (eventName, payload) => {
      try {
        track(eventName, payload)
      } catch (err) {
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, err)
      }
    }
  }

  // Format cents → money string. Prefers the Spectrum SDK formatter (honours the
  // store's money_format); falls back to a currency-aware Intl format.
  function formatMoney(cents, moneyFormat, currency) {
    if (cents == null) return ''
    const sdk = window.Spectrum && window.Spectrum.money
    if (sdk && typeof sdk.format === 'function') {
      try {
        return sdk.format(cents, moneyFormat || undefined)
      } catch (_) {
        /* fall through */
      }
    }
    const value = Number(cents) / 100
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      }).format(value)
    } catch (_) {
      return value.toFixed(2)
    }
  }

  class SaiBuyBoxElement extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return
      this._mounted = true

      const tag = this.querySelector('script[data-spectrum-vghxskvp-pool]')
      if (!tag) return
      let pool
      try {
        pool = JSON.parse(tag.textContent || '{}')
      } catch {
        return
      }
      this._pool = pool
      this._config = pool.config || {}
      this._shop = pool.shop || {}
      this._track = noopTrack
      this._qty = 1
      this._selected = this._buildInitialSelection()
      this._current = this._resolveVariant(this._selected)

      const snippetApi = window.__spectrumAi && window.__spectrumAi.snippet
      if (snippetApi && typeof snippetApi.bind === 'function') {
        const handles = snippetApi.bind(this, () => {})
        if (handles && handles.track) this._track = safeTrack(handles.track)
      }

      this._bindSwatches()
      this._bindQuantity()
      this._bindPurchase()
    }

    disconnectedCallback() {
      // Cart drawers move/re-render nodes; allow a re-inserted element to re-bind.
      this._mounted = false
    }

    _buildInitialSelection() {
      const options = this._pool.options || []
      const selected = {}
      for (const opt of options) selected[opt.position] = opt.selectedValue
      return selected
    }

    _resolveVariant(selection) {
      const variants = this._pool.variants || []
      for (const v of variants) {
        if (
          v.option1 === (selection[1] ?? null) &&
          (v.option2 ?? null) === (selection[2] ?? null) &&
          (v.option3 ?? null) === (selection[3] ?? null)
        ) {
          return v
        }
      }
      // Fallback: the available variant matching the most positions.
      let best = null
      let bestScore = -1
      for (const v of variants) {
        if (!v.available) continue
        let score = 0
        if (v.option1 === selection[1]) score++
        if ((v.option2 ?? null) === (selection[2] ?? null)) score++
        if ((v.option3 ?? null) === (selection[3] ?? null)) score++
        if (score > bestScore) {
          bestScore = score
          best = v
        }
      }
      return best || variants[0] || null
    }

    // ── Swatches ──

    _bindSwatches() {
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-spectrum-value]')
        if (!btn) return
        e.preventDefault()
        if (btn.classList.contains('is-oos')) return
        const pos = Number.parseInt(btn.dataset.spectrumPosition, 10)
        const val = btn.dataset.spectrumValue
        if (!Number.isFinite(pos) || val == null) return
        this._pickValue(pos, val)
      })
    }

    _pickValue(position, value) {
      if (this._selected[position] === value) return
      this._selected[position] = value
      const variant = this._resolveVariant(this._selected)
      if (!variant) return
      this._current = variant
      this._applySelectionUI(variant)
      this._applyPrice(variant)
      this._applyAvailability(variant)
      this._track(`${FEATURE_SLUG}:option_select`, {
        product_id: this._pool.productId,
        variant_id: variant.id,
        option_position: position,
        option_value: value,
      })
    }

    _applySelectionUI(variant) {
      const axes = this.querySelectorAll('[data-axis-position]')
      for (const axis of axes) {
        const pos = Number.parseInt(axis.dataset.axisPosition, 10)
        const selectedVal = variant[`option${pos}`]
        for (const btn of axis.querySelectorAll('[data-spectrum-value]')) {
          const isSel = btn.dataset.spectrumValue === selectedVal
          btn.classList.toggle('is-selected', isSel)
          btn.setAttribute('aria-pressed', isSel ? 'true' : 'false')
        }
        const label = axis.querySelector('[data-spectrum-selected-value]')
        if (label) label.textContent = selectedVal ?? ''
      }
      this.dataset.currentVariantId = String(variant.id)
    }

    _applyPrice(variant) {
      const currentEl = this.querySelector('[data-spectrum-price-current]')
      if (currentEl) {
        currentEl.textContent =
          variant.priceFormatted || formatMoney(variant.price, this._shop.moneyFormat, this._shop.currency)
      }

      const hasCompare =
        variant.compareAtPrice != null && Number(variant.compareAtPrice) > Number(variant.price)

      const compareEl = this.querySelector('[data-spectrum-price-compare]')
      if (compareEl) {
        if (hasCompare && this._config.showCompareAt !== false) {
          compareEl.hidden = false
          compareEl.textContent =
            variant.compareAtFormatted ||
            formatMoney(variant.compareAtPrice, this._shop.moneyFormat, this._shop.currency)
        } else {
          compareEl.hidden = true
        }
      }

      const savingsEl = this.querySelector('[data-spectrum-savings]')
      if (savingsEl) {
        const mode = this._config.savingsDisplay || 'percent'
        if (hasCompare && mode !== 'none') {
          savingsEl.hidden = false
          if (mode === 'percent') {
            const pct = Math.round(
              ((Number(variant.compareAtPrice) - Number(variant.price)) /
                Number(variant.compareAtPrice)) *
                100,
            )
            savingsEl.textContent = `-${pct}%`
          } else {
            const amt = Number(variant.compareAtPrice) - Number(variant.price)
            savingsEl.textContent = `Save ${formatMoney(amt, this._shop.moneyFormat, this._shop.currency)}`
          }
        } else {
          savingsEl.hidden = true
        }
      }
    }

    _applyAvailability(variant) {
      const available = !!(variant && variant.available)
      const atc = this.querySelector('[data-spectrum-atc]')
      if (atc) {
        atc.disabled = !available
        atc.setAttribute('aria-disabled', available ? 'false' : 'true')
        const label = atc.querySelector('[data-spectrum-atc-label]')
        if (label && !available) label.textContent = 'Sold out'
      }
      const buynow = this.querySelector('[data-spectrum-buynow]')
      if (buynow) {
        buynow.disabled = !available
        buynow.setAttribute('aria-disabled', available ? 'false' : 'true')
      }
    }

    // ── Quantity ──

    _bindQuantity() {
      this._qtyInput = this.querySelector('[data-spectrum-qty-input]')
      const dec = this.querySelector('[data-spectrum-qty-dec]')
      const inc = this.querySelector('[data-spectrum-qty-inc]')
      if (dec) dec.addEventListener('click', () => this._setQty(this._qty - 1))
      if (inc) inc.addEventListener('click', () => this._setQty(this._qty + 1))
      if (this._qtyInput) {
        this._qtyInput.addEventListener('change', () =>
          this._setQty(Number.parseInt(this._qtyInput.value, 10)),
        )
      }
      this._setQty(1)
    }

    _setQty(next) {
      let q = Number.isFinite(next) ? Math.floor(next) : 1
      if (q < 1) q = 1
      if (q > QTY_MAX) q = QTY_MAX
      this._qty = q
      if (this._qtyInput) this._qtyInput.value = String(q)
      const dec = this.querySelector('[data-spectrum-qty-dec]')
      if (dec) dec.disabled = q <= 1
    }

    // ── Purchase ──

    _bindPurchase() {
      const atc = this.querySelector('[data-spectrum-atc]')
      if (atc) atc.addEventListener('click', () => this._addToCart(atc, false))
      const buynow = this.querySelector('[data-spectrum-buynow]')
      if (buynow) buynow.addEventListener('click', () => this._addToCart(buynow, true))
    }

    async _addToCart(btn, checkout) {
      const variant = this._current
      if (!variant || !variant.available) return
      const cart = window.Spectrum && window.Spectrum.cart
      if (!cart || typeof cart.add !== 'function') {
        console.warn(`[${FEATURE_SLUG}] window.Spectrum.cart unavailable — cannot add to cart`)
        return
      }

      const quantity = this._qty
      const item = { id: variant.id, quantity }
      this._setLoading(btn, true)
      this._track(`${FEATURE_SLUG}:${checkout ? 'buy_now' : 'add_to_cart'}`, {
        product_id: this._pool.productId,
        variant_id: variant.id,
        quantity,
      })

      try {
        if (checkout) {
          await cart.add(item, { sourceId: `spectrum-${SNIPPET_ID}` })
          // Never navigate the Studio editor preview iframe.
          if (window.__spectrumAi && window.__spectrumAi.__preview === true) {
            console.warn(`[${FEATURE_SLUG}] checkout navigation suppressed in Studio preview`)
            this._setLoading(btn, false)
            return
          }
          // Leave the spinner running through the real navigation — the page
          // unloads before it would otherwise clear.
          window.location.href = '/checkout'
          return
        }
        if (typeof cart.addAndOpen === 'function') {
          await cart.addAndOpen(item, { sourceId: `spectrum-${SNIPPET_ID}` })
        } else {
          await cart.add(item, { sourceId: `spectrum-${SNIPPET_ID}` })
        }
        this._setLoading(btn, false)
      } catch (err) {
        console.warn(`[${FEATURE_SLUG}] add to cart failed`, err)
        this._setLoading(btn, false)
      }
    }

    _setLoading(btn, loading) {
      if (!btn) return
      btn.disabled = loading || !(this._current && this._current.available)
      const spinner = btn.querySelector('[data-spectrum-spinner]')
      if (spinner) {
        spinner.hidden = !loading
        btn.setAttribute('aria-busy', loading ? 'true' : 'false')
      }
    }
  }

  if (!customElements.get('sai-vghxskvp')) {
    customElements.define('sai-vghxskvp', SaiBuyBoxElement)
  }
})()
